import { CompactButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { Form } from '@/components/forms';
import { PrimaryLayout } from '@/layouts/primary';
import { Select, Textarea } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { useCreateSalesforceCaseMutation } from './graphql.generated';
import { issueTypeOptions, ReportIssueFormValues, validationSchema } from './utils';

export const ReportIssuePage = () => {
	const [createSupportCase, { loading }] = useCreateSalesforceCaseMutation();
	const navigate = useNavigate();
	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={() => navigate({ to: '/' })}>
				Close
			</CompactButton>
		),
		[navigate]
	);

	const initialValues = {
		typeOfIssue: undefined,
		comments: '',
	} as unknown as ReportIssueFormValues;

	const onSubmit = useCallback(
		async (values: ReportIssueFormValues) => {
			await createSupportCase({
				variables: {
					createSalesforceCaseInput: {
						reason: values.typeOfIssue,
						subject: values.typeOfIssue,
						description: values.comments,
					},
				},
			});
			navigate({ to: '/help' });
		},
		[createSupportCase, navigate]
	);

	const formRef = useRef<FormikProps<ReportIssueFormValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const submitButton = useMemo(
		() => (
			<StandardButton onClick={triggerSubmit} loading={loading}>
				Submit
			</StandardButton>
		),
		[triggerSubmit, loading]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Report an Issue', children: closeButton }}
			footer={{ children: submitButton }}
		>
			<ContentPanel>
				<Form<ReportIssueFormValues>
					initialValues={initialValues}
					onSubmit={onSubmit}
					innerRef={formRef}
					validationSchema={validationSchema}
				>
					{({ getFieldProps, errors, touched, setFieldValue, values }) => (
						<>
							<Select
								label="Type of Issue"
								placeholder="Pease select..."
								data={issueTypeOptions}
								value={values.typeOfIssue}
								onChange={(value) => setFieldValue('typeOfIssue', value)}
								error={touched.typeOfIssue && errors.typeOfIssue}
								mb="md"
								withScrollArea={false}
							/>
							<Textarea
								label="Comments"
								placeholder="Enter any additional comments"
								{...getFieldProps('comments')}
								error={touched.comments && errors.comments}
								mb="md"
								rows={14}
							/>
						</>
					)}
				</Form>
			</ContentPanel>
		</PrimaryLayout>
	);
};
